import React, { useState, useEffect, useRef, useMemo, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  customerPaymentOrBooking,
  getAllStringsFromInventory,
  getCustomerProfile,
  getCustomerAddresses,
  getActiveDiscountApi,
  getExpressSettingsApi,
} from "../../../api";
import { getDubaiDate } from "../../../utils/date";
import { useBooking } from "../../../context/BookingContext";
import PayInStoreButton from "../../PayInStore/PayInStoreButton";
import useAuthGuard from "../../../hooks/useAuthGuard";
import termsAndConditions from "../../../Constants/termsAndConditions";
import AddressForm from "../../Booking/AddressForm";
import styles from "./CustomerBookingFinal.module.css";

function joinAddress(...parts) {
  return parts
    .map((p) => (p ?? "").toString().trim())
    .filter((p) => p && p.toLowerCase() !== "null")
    .join(", ");
}

function isByosString(stringObj) {
  if (!stringObj) return true;
  if (stringObj.string_id === "byos" || stringObj.string_id === 0 || stringObj.string_id === "0") return true;
  const name = (stringObj.name || stringObj.string_name || "").toLowerCase().trim();
  return name.includes("bring own") || name.includes("byos");
}

const CustomerBookingFinal = () => {
  useAuthGuard();
  const { address: contextAddress, customer } = useBooking();
  const navigate = useNavigate();
  const location = useLocation();
  const [errorMessage, setErrorMessage] = useState("");
  const termsRef = useRef(null);
  const [showingToast, setShowingToast] = useState(false);
  const clearErrTimerRef = useRef(null);
  const [newRacketName, setNewRacketName] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [selectedBookingIndex, setSelectedBookingIndex] = useState(null);

  const [selectedStringId, setSelectedStringId] = useState(null);
  const [selectedStringIdCross, setSelectedStringIdCross] = useState(null);
  const [prestretchEnabledCross, setPrestretchEnabledCross] = useState(false);
  const [prestretchPercentCross, setPrestretchPercentCross] = useState("");
  const [hybridTab, setHybridTab] = useState("main");
  const [selectedStringType, setSelectedStringType] = useState("");

  const [totalCost, setTotalCost] = useState("");
  const [isTensionModalOpen, setIsTensionModalOpen] = useState(false);
  const [tensionOption, setTensionOption] = useState("");
  const [tensionOptionMain, setTensionOptionMain] = useState("");
  const [tensionOptionCross, setTensionOptionCross] = useState("");
  const [tensionModalTarget, setTensionModalTarget] = useState("all");
  const [tensionMain, setTensionMain] = useState("");
  const [tensionCross, setTensionCross] = useState("");
  const [tensionUnit, setTensionUnit] = useState("lbs");
  const [prestretchEnabled, setPrestretchEnabled] = useState(false);
  const [prestretchPercent, setPrestretchPercent] = useState("");
  const [isBreakdownPopupOpen, setIsBreakdownPopupOpen] = useState(false);
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [showTermsPopup, setShowTermsPopup] = useState(false);
  const redirectTimerRef = useRef(null);

  const [showAddStringInput, setShowAddStringInput] = useState(false);
  const [strings, setStrings] = useState([]);
  const [fetchedCustomerId, setFetchedCustomerId] = useState(null);
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);
  
  // Coach coupon state
  const [couponCode, setCouponCode] = useState("");
  const [couponApplied, setCouponApplied] = useState(false);
  const [couponDiscount, setCouponDiscount] = useState(0);
  const [isCoach, setIsCoach] = useState(false);
  const [coachCouponCode, setCoachCouponCode] = useState("");

  const {
    customerId: stateCustomerId,
    services = "Stringing Service",
    bookingDate = "Not Provided",
    previousBookings = [],
    freeServices = 0,
    usedServices = 0,
    customerName: passedCustomerName,
    pickupService = "in-store",
    instoreLocation = null,
    rp_id = null,
    address: passedAddress = null,
  } = location.state || {};

  // Use customerId from state, or fetch from profile if not available
  const customerId = stateCustomerId || fetchedCustomerId;

  // Fetch customer profile on mount
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const response = await getCustomerProfile();
        setIsCoach(response.data.is_coach || false);
        setCoachCouponCode(response.data.coach_coupon_code || "");
        if (!stateCustomerId) {
          setFetchedCustomerId(response.data.customer_id);
        }
      } catch (error) {
        console.error("Error fetching customer profile:", error);
      }
    };

    fetchProfile();
  }, [stateCustomerId]);

  const [activeDiscount, setActiveDiscount] = useState(null);

  // Fetch strings from inventory and active discount
  useEffect(() => {
    const fetchStringsAndDiscount = async () => {
      try {
        const [data, discountRes] = await Promise.all([
          getAllStringsFromInventory(),
          getActiveDiscountApi()
        ]);
        setStrings(data);
        if (discountRes.data && discountRes.data.active) {
          setActiveDiscount(discountRes.data.discount);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      }
    };

    fetchStringsAndDiscount();
  }, []);

  const [expressSettings, setExpressSettings] = useState({
    is_express_enabled: true,
    marina_express_enabled: false,
    cutoff_hour: 17,
  });

  useEffect(() => {
    getExpressSettingsApi()
      .then((res) => {
        if (res?.data) setExpressSettings(res.data);
      })
      .catch((err) => {
        console.error("Could not fetch express settings in CustomerBookingFinal:", err);
      });
  }, []);

  // Use passed address from navigation state, fallback to context address
  const [address, setAddress] = useState(passedAddress || contextAddress || "");

  // Fetch and auto-select default address if no address is provided
  useEffect(() => {
    const fetchDefaultAddress = async () => {
      if (!address || !address.trim()) {
        try {
          const response = await getCustomerAddresses();
          const addresses = response.data;
          const defaultAddr = addresses.find((a) => a.is_default === 1) || addresses[0];
          if (defaultAddr) {
            const formattedAddress = joinAddress(
              defaultAddr.address_line,
              defaultAddr.area,
              defaultAddr.pincode,
              defaultAddr.state,
              defaultAddr.country
            );
            setAddress(formattedAddress);
          }
        } catch (error) {
          console.error("Error fetching addresses:", error);
        }
      }
    };

    fetchDefaultAddress();
  }, [address]);

  const customerName =
    passedCustomerName ||
    (customer ? `${customer.first_name} ${customer.last_name}` : "N/A");

  const formatPickupService = (service) => {
    switch (service) {
      case "pickup":
        return "Pickup Only";
      case "delivery":
        return "Delivery Only";
      case "pickup/delivery":
        return "Both Pickup & Delivery";
      case "in-store":
        return "In-Store (No Charge)";
      case "express-pickup":
        return "Express Pickup";
      case "express-delivery":
        return "Express Delivery";
      case "express-pickup/delivery":
        return "Express Pickup & Delivery";
      default:
        return service;
    }
  };

  // Load bookings then normalize so every row has serviceKind
  const [bookings, setBookings] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("bookings") || "[]");
      const base = stored.length ? stored : previousBookings;
      return (Array.isArray(base) ? base : []).map((b) => {
        if (b.serviceKind) return b;
        return { ...b, serviceKind: "string" };
      });
    } catch {
      return (previousBookings || []).map((b) =>
        b.serviceKind ? b : { ...b, serviceKind: "string" }
      );
    }
  });

  useEffect(() => {
    localStorage.setItem("bookings", JSON.stringify(bookings));
  }, [bookings]);

  useEffect(() => {
    return () => {
      if (redirectTimerRef.current) {
        clearTimeout(redirectTimerRef.current);
        redirectTimerRef.current = null;
      }
      if (clearErrTimerRef.current) {
        clearTimeout(clearErrTimerRef.current);
      }
    };
  }, []);

  const showError = (msg, { focusTerms = false } = {}) => {
    setErrorMessage(msg);
    if (focusTerms) {
      setTermsError(true);
    }
    setShowingToast(true);
    if (clearErrTimerRef.current) clearTimeout(clearErrTimerRef.current);
    clearErrTimerRef.current = setTimeout(() => {
      setShowingToast(false);
      setErrorMessage("");
    }, 3500);
    if (focusTerms && termsRef.current) {
      termsRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      termsRef.current.classList.add(styles.pulseHighlight);
      termsRef.current.classList.add("pulseHighlight");
      setTimeout(
        () => {
          termsRef.current?.classList.remove(styles.pulseHighlight);
          termsRef.current?.classList.remove("pulseHighlight");
        },
        1600
      );
    }
  };

  const lockedStringType = useMemo(() => {
    const b = bookings.find((x) => x?.stringType);
    return b?.stringType || null;
  }, [bookings]);

  const getOriginalIndividualCost = useCallback(
    (booking) => {
      const stringDetail = strings.find(
        (item) => item.string_id === booking.stringId
      );
      if (!stringDetail) return 0;

      const isExpress = booking.stringType === "Express";

      if (booking.isHybrid) {
        const stringDetailCross = strings.find(
          (item) => item.string_id === booking.stringIdCross
        );
        const isMainByos = isByosString(stringDetail);
        const isCrossByos = isByosString(stringDetailCross);
        const bothByos = isMainByos && isCrossByos;

        if (bothByos) {
          return isExpress ? 100 : 75;
        }

        // Consolidated hybrid pricing: 200 regular / 225 express
        return isExpress ? 225 : 200;
      }

      return isExpress
        ? stringDetail.basePrice + 25
        : stringDetail.basePrice;
    },
    [strings]
  );

  const getIndividualCost = useCallback(
    (booking) => {
      const stringDetail = strings.find(
        (item) => item.string_id === booking.stringId
      );
      if (!stringDetail) return 0;

      const isExpress = booking.stringType === "Express";

      if (booking.isHybrid) {
        const stringDetailCross = strings.find(
          (item) => item.string_id === booking.stringIdCross
        );
        const isMainByos = isByosString(stringDetail);
        const isCrossByos = isByosString(stringDetailCross);
        const bothByos = isMainByos && isCrossByos;

        if (bothByos) {
          if (booking.freeService) {
            return isExpress ? 25 : 0;
          }
          if (activeDiscount && !couponApplied) {
            return isExpress ? Number(activeDiscount.express_price) : Number(activeDiscount.regular_price);
          }
          return isExpress ? 100 : 75;
        }

        // Consolidated hybrid pricing: 200 regular / 225 express
        if (booking.freeService) {
          return isExpress ? 150 : 125;
        }
        if (activeDiscount && !couponApplied) {
          const regDiscount = 75 - Number(activeDiscount.regular_price);
          const expDiscount = 100 - Number(activeDiscount.express_price);
          return isExpress ? Math.max(225 - expDiscount, 0) : Math.max(200 - regDiscount, 0);
        }
        return isExpress ? 225 : 200;
      }

      if (booking.freeService) {
        return isExpress
          ? Math.max(stringDetail.basePrice - 75 + 25, 0)
          : Math.max(stringDetail.basePrice - 75, 0);
      }
      if (activeDiscount && !couponApplied) {
        return isExpress
          ? Math.max(stringDetail.basePrice - 75 + Number(activeDiscount.express_price), 0)
          : Math.max(stringDetail.basePrice - 75 + Number(activeDiscount.regular_price), 0);
      }
      return isExpress
        ? stringDetail.basePrice + 25
        : stringDetail.basePrice;
    },
    [strings, activeDiscount, couponApplied]
  );

  const originalStringCost = useMemo(() => {
    return bookings.reduce((acc, b) => acc + getOriginalIndividualCost(b), 0);
  }, [bookings, getOriginalIndividualCost]);

  const discountDetails = useMemo(() => {
    if (!activeDiscount || couponApplied) return null;
    let regularCount = 0;
    let expressCount = 0;
    let totalSaved = 0;

    const regDiscount = 75 - Number(activeDiscount.regular_price);
    const expDiscount = 100 - Number(activeDiscount.express_price);

    bookings.forEach((b) => {
      if (b.freeService) return;
      const isExpress = b.stringType === "Express";
      if (isExpress) {
        expressCount += 1;
        totalSaved += expDiscount;
      } else {
        regularCount += 1;
        totalSaved += regDiscount;
      }
    });

    return {
      name: activeDiscount.discount_name,
      regularCount,
      expressCount,
      regularDiscount: regDiscount,
      expressDiscount: expDiscount,
      totalSaved,
    };
  }, [bookings, activeDiscount, couponApplied]);

  const breakdown = useMemo(() => {
    const stringCost = bookings.reduce(
      (acc, b) => acc + getIndividualCost(b),
      0
    );

    let pickupFee = 0;
    if (pickupService === "pickup" || pickupService === "delivery")
      pickupFee = 30;
    else if (pickupService === "pickup/delivery") pickupFee = 60;
    else if (
      pickupService === "express-pickup" ||
      pickupService === "express-delivery"
    )
      pickupFee = 50;
    else if (pickupService === "express-pickup/delivery") pickupFee = 100;

    return {
      stringCost,
      pickupFee,
      grandTotal: stringCost + pickupFee,
    };
  }, [bookings, pickupService, getIndividualCost]);

  const overallTotal = breakdown.grandTotal;

  const formatTension = (booking) => {
    if (!booking.tensionType) return "—";
    if (booking.tensionType === "support") return "Talk with support";
    if (booking.isHybrid) {
      if (booking.tensionType === "standard") return "Main: Standard, Cross: Standard";
      const unit = booking.tensionUnit || "lbs";
      const mainPrestretch = booking.prestretch ? ` (Prestretch: ${booking.prestretchPercent ?? "—"}%)` : "";
      const crossPrestretch = booking.prestretchCross ? ` (Prestretch: ${booking.prestretchPercentCross ?? "—"}%)` : "";
      const mainStr = booking.tensionMain != null ? `${booking.tensionMain}${unit}${mainPrestretch}` : "Standard";
      const crossStr = booking.tensionCross != null ? `${booking.tensionCross}${unit}${crossPrestretch}` : "Standard";
      return `Main: ${mainStr}, Cross: ${crossStr}`;
    }
    if (booking.tensionType === "standard") return "Standard";
    if (booking.tensionType === "custom") {
      const unit = booking.tensionUnit || "lbs";
      const main = booking.tensionMain ?? "—";
      const cross = booking.tensionCross ?? "—";
      let label = `${main}${unit} main / ${cross}${unit} cross`;
      if (booking.prestretch) {
        label += `, Prestretch: ${booking.prestretchPercent ?? "—"}%`;
      }
      return label;
    }
    return "—";
  };

  const handleEdit = (index) => {
    const bookingToEdit = bookings[index];
    if (!bookingToEdit) return;
    setSelectedBookingIndex(index);

    setSelectedStringId(bookingToEdit.stringId || null);
    setSelectedStringIdCross(bookingToEdit.stringIdCross || null);
    setSelectedStringType(bookingToEdit.stringType || "");
    setTotalCost(getIndividualCost(bookingToEdit) || 0);

    setTensionOption(bookingToEdit.tensionType || "");
    const initialMainOpt = bookingToEdit.tensionOptionMain || (bookingToEdit.tensionMain != null ? "custom" : bookingToEdit.tensionType || "standard");
    const initialCrossOpt = bookingToEdit.tensionOptionCross || (bookingToEdit.tensionCross != null ? "custom" : bookingToEdit.tensionType || "standard");
    setTensionOptionMain(initialMainOpt);
    setTensionOptionCross(initialCrossOpt);
    setTensionModalTarget("main");
    setTensionMain(bookingToEdit.tensionMain != null ? String(bookingToEdit.tensionMain) : "");
    setTensionCross(bookingToEdit.tensionCross != null ? String(bookingToEdit.tensionCross) : "");
    setTensionUnit(bookingToEdit.tensionUnit || "lbs");
    setPrestretchEnabled(!!bookingToEdit.prestretch);
    setPrestretchPercent(bookingToEdit.prestretchPercent != null ? String(bookingToEdit.prestretchPercent) : "");
    setPrestretchEnabledCross(!!bookingToEdit.prestretchCross);
    setPrestretchPercentCross(bookingToEdit.prestretchPercentCross != null ? String(bookingToEdit.prestretchPercentCross) : "");
    setHybridTab("main");

    setEditModalOpen(true);
  };

  const handleSaveEdit = () => {
    if (selectedBookingIndex === null) return;

    const updated = [...bookings];
    const b = updated[selectedBookingIndex];
    if (!b) return;
    const isHybrid = !!b.isHybrid;

    if (isHybrid) {
      if (!tensionOptionMain) {
        showError("Please select a tension option for your main string.");
        return;
      }
      if (tensionOptionMain === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          showError("Please enter a valid main tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          showError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
      if (!tensionOptionCross) {
        showError("Please select a tension option for your cross string.");
        return;
      }
      if (tensionOptionCross === "custom") {
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          showError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabledCross && (!prestretchPercentCross || parseFloat(prestretchPercentCross) <= 0)) {
          showError("Please enter a valid cross prestretch percentage.");
          return;
        }
      }
    } else {
      if (tensionOption === "custom") {
        if (!tensionMain || parseFloat(tensionMain) <= 0) {
          showError("Please enter a valid main tension value.");
          return;
        }
        if (!tensionCross || parseFloat(tensionCross) <= 0) {
          showError("Please enter a valid cross tension value.");
          return;
        }
        if (prestretchEnabled && (!prestretchPercent || parseFloat(prestretchPercent) <= 0)) {
          showError("Please enter a valid main prestretch percentage.");
          return;
        }
      }
    }

    if (!selectedStringId || !selectedStringType) return;
    if (isHybrid && (!selectedStringId || !selectedStringIdCross)) {
      showError("Please select both a main and cross string for hybrid setup.");
      return;
    }

    const stringDetail = strings.find((s) => s.string_id === selectedStringId);

    b.stringId = selectedStringId;
    if (stringDetail) {
      b.stringName = stringDetail.name;
      b.stringBrand = stringDetail.brand;
    }
    b.stringType = selectedStringType;
    if (isHybrid) {
      b.stringIdCross = selectedStringIdCross;
    }

    const isMainCustom = isHybrid ? tensionOptionMain === "custom" : tensionOption === "custom";
    const isCrossCustom = isHybrid ? tensionOptionCross === "custom" : tensionOption === "custom";
    const computedTensionType = !isHybrid
      ? tensionOption
      : (tensionOptionMain === "standard" && tensionOptionCross === "standard")
        ? "standard"
        : (tensionOptionMain === "support" && tensionOptionCross === "support")
          ? "support"
          : "custom";

    b.tensionType = computedTensionType;
    b.tensionOptionMain = isHybrid ? tensionOptionMain : tensionOption;
    b.tensionOptionCross = isHybrid ? tensionOptionCross : tensionOption;
    b.tensionMain = isMainCustom && tensionMain ? parseFloat(tensionMain) : null;
    b.tensionCross = isCrossCustom && tensionCross ? parseFloat(tensionCross) : null;
    b.tensionUnit = (isMainCustom || isCrossCustom) ? tensionUnit : null;
    b.prestretch = isMainCustom && prestretchEnabled ? 1 : 0;
    b.prestretchPercent = isMainCustom && prestretchEnabled && prestretchPercent ? parseFloat(prestretchPercent) : null;
    b.prestretchCross = isHybrid && isCrossCustom && prestretchEnabledCross ? 1 : 0;
    b.prestretchPercentCross = isHybrid && isCrossCustom && prestretchEnabledCross && prestretchPercentCross ? parseFloat(prestretchPercentCross) : null;

    const cost = getIndividualCost(b);
    b.stringPrice = cost;
    b.totalCost = cost;

    setBookings(updated);
    localStorage.setItem("bookings", JSON.stringify(updated));
    setEditModalOpen(false);
  };

  const handleDelete = (index) => {
    const updated = bookings.filter((_, i) => i !== index);
    setBookings(updated);
  };

  // Add More STRING only when none is free
  const handleAddMore = () => {
    if (!newRacketName.trim()) {
      showError("Please enter a racket name before adding more.");
      return;
    }

    navigate("/customer/dashboard/booking-form", {
      state: {
        customerId,
        previousBookings: bookings,
        customerName,
        bookingDate,
        pickupService,
        instoreLocation,
        services,
        racketName: newRacketName,
        freeServices,
        usedServices,
        isAddMore: true,
        stringTypeConstraint: lockedStringType,
        address,
        lockServiceSelection: true,
      },
    });
  };

  const generateUniqueCartId = () => {
    const prefix = "TXN";
    return `${prefix}-${Date.now()}-${Math.floor(Math.random() * 1000)}`;
  };

  const customerEmail =
    location.state?.customerEmail ??
    customer?.email ??
    customer?.email_address ??
    customer?.primary_email ??
    null;

  const handleProceedToPay = async () => {
    if (!agreedToTerms) {
      showError("Please agree to the Terms & Conditions before proceeding.");
      return;
    }
    if (bookings.length === 0) {
      showError("No bookings to process.");
      return;
    }

    // Validate address for non-in-store pickups
    if (pickupService !== "in-store" && !address?.trim()) {
      showError("Please add a delivery/pickup address.");
      return;
    }

    setIsProcessing(true);

    // Validate bookings
    for (const b of bookings) {
      if (!b.stringId) {
        showError("Please select a string for all string bookings.");
        setIsProcessing(false);
        return;
      }
      if (!b.stringType) {
        showError(
          "Please select a string type (Regular/Express) for all string bookings."
        );
        setIsProcessing(false);
        return;
      }
    }

    // Validate Express availability (Cutoff time in Dubai & Marina restriction)
    const { ymd: todayDubai, hour: currentHourDubai } = getDubaiDate();
    const cutoffHour = expressSettings.cutoff_hour ?? 17;
    const isPastCutoff = (bookingDate === todayDubai || !bookingDate) && currentHourDubai >= cutoffHour;
    const isMarina = pickupService === "in-store" && instoreLocation?.toLowerCase().includes("marina");
    const isDubaiHills = pickupService === "in-store" && instoreLocation?.toLowerCase().includes("dubai");

    for (const b of bookings) {
      if (String(b.stringType || "").toLowerCase() === "express") {
        if (!expressSettings.is_express_enabled) {
          showError("Express service is currently disabled. Please select Regular speed.");
          setIsProcessing(false);
          return;
        }
        if (isMarina && !expressSettings.marina_express_enabled) {
          showError("Express service is not available at Marina location. Please select Regular speed.");
          setIsProcessing(false);
          return;
        }
        if (isDubaiHills && !expressSettings.dubaihills_express_enabled) {
          showError("Express service is not available at Dubai Hills location. Please select Regular speed.");
          setIsProcessing(false);
          return;
        }
        if (isPastCutoff) {
          const formattedHour = cutoffHour > 12 ? `${cutoffHour - 12}:00 PM` : `${cutoffHour}:00 AM`;
          showError(`Express service cutoff (${formattedHour} UAE time) has passed for today. Please select Regular speed.`);
          setIsProcessing(false);
          return;
        }
      }
    }

    const cartId = generateUniqueCartId();

    const bookingRequests = bookings.map((b) => {
      const baseRequest = {
        bigcommerce_customer_id: customerId,
        service_kind: "string",
        string_id: b.stringId,
        booking_date: bookingDate,
        pickup_option: pickupService,
        delivery_address: address || null,
        racket_name: b.racketName,
        total_cost: getIndividualCost(b),
        cart_id: cartId,
        freeService: b.freeService || false,
        instore_location: pickupService === "in-store" ? instoreLocation : null,
        rp_id: b.rp_id || rp_id || null,
        customer_name: customerName,
        customer_email: customerEmail,
        tension_type: b.tensionType || null,
        tension_main: b.tensionType === "custom" && b.tensionMain != null ? parseFloat(b.tensionMain) : null,
        tension_cross: b.tensionType === "custom" && b.tensionCross != null ? parseFloat(b.tensionCross) : null,
        tension_unit: b.tensionType === "custom" ? (b.tensionUnit || null) : null,
        prestretch: b.tensionType === "custom" && b.prestretch ? 1 : 0,
        prestretch_percent: b.tensionType === "custom" && b.prestretch && b.prestretchPercent != null ? parseFloat(b.prestretchPercent) : null,
      };

      // Add string details
      if (b.stringId) {
        const stringDetail = strings.find((s) => s.string_id === b.stringId);
        if (stringDetail) {
          const serviceSpeed = b.stringType || "Regular";
          
          if (b.isHybrid) {
            const stringCross = strings.find((s) => s.string_id === b.stringIdCross);
            const finalCost = getIndividualCost(b);
            
            baseRequest.string_name = stringDetail.name;
            baseRequest.string_brand = stringDetail.brand;
            baseRequest.string_price = 0;
            
            baseRequest.is_hybrid = 1;
            baseRequest.string_id_cross = b.stringIdCross;
            baseRequest.string_name_cross = stringCross ? stringCross.name : null;
            baseRequest.string_brand_cross = stringCross ? stringCross.brand : null;
            baseRequest.string_price_cross = 0;
            
            baseRequest.prestretch_cross = b.prestretchCross ? 1 : 0;
            baseRequest.prestretch_percent_cross = b.prestretchCross && b.prestretchPercentCross != null ? parseFloat(b.prestretchPercentCross) : null;
            
            baseRequest.service_speed = serviceSpeed;
            baseRequest.service_fee = 0;
            baseRequest.total_cost = finalCost;
          } else {
            const stringPrice = getIndividualCost(b);
            baseRequest.string_name = stringDetail.name;
            baseRequest.string_brand = stringDetail.brand;
            baseRequest.string_price = stringPrice;
            baseRequest.service_speed = serviceSpeed;
            baseRequest.service_fee = 0; // Service fee is added once for entire order
            baseRequest.total_cost = stringPrice;
            baseRequest.is_hybrid = 0;
          }
        }
      }

      return baseRequest;
    });

    try {
      const response = await customerPaymentOrBooking({
        bookings: bookingRequests,
        pickup_fee: breakdown?.pickupFee || 0,
        coupon_code: couponCode || undefined, // Include coupon code if provided
      });
      const data = response.data;

      if (response.status === 200 || response.status === 201) {
        if (data.bookings && data.bookings.length > 0) {
          alert("Your booking has been created successfully!");
          setBookings([]);
          localStorage.removeItem("bookings");

          navigate("/customer-payment-success?freeBooking=1", {
            state: {
              bookingIds: data.bookings.map((b) => b.booking_id),
              customerName,
              totalCost: 0,
              message: "Your booking has been successfully created.",
            },
          });
        } else if (data.payment_url) {
          window.location.assign(data.payment_url);
        } else {
          showError("Something went wrong!");
        }
      } else {
        console.error("Error creating booking/payment:", data);
        showError("Failed to create booking or process payment.");
      }
    } catch (error) {
      console.error("Booking Submission Error:", error);
      showError("Error processing bookings.");
    } finally {
      setIsProcessing(false);
    }
  };

  const openBreakdown = () => setIsBreakdownPopupOpen(true);

  return (
    <div className={styles.container}>
      {isProcessing && (
        <div className={styles.loadingOverlay}>
          <div className={styles.loadingContent}>
            <div className={styles.loader}></div>
            <p>Please wait...</p>
            <p className="subText">Your booking is being processed.</p>
          </div>
        </div>
      )}

      {errorMessage && (
        <div
          className={[
            styles.errorMessage,
            showingToast ? styles.toastVisible : null,
          ]
            .filter(Boolean)
            .join(" ")}
          role="status"
          aria-live="polite"
        >
          {errorMessage}
        </div>
      )}

      <h2 className={styles.title}>Booking Summary</h2>
      <p>
        <strong>Customer Name:</strong> {customerName || "N/A"}
      </p>
      <p>
        <strong>Booking Date:</strong> {bookingDate}
      </p>
      <p>
        <strong>Pickup Service:</strong>{" "}
        {formatPickupService(pickupService || "in-store")}
      </p>
      {pickupService === "in-store" && instoreLocation && (
        <p>
          <strong>Store Location:</strong> {instoreLocation}
        </p>
      )}
      {pickupService !== "in-store" && (
        <div className={styles.addressSection}>
          <p>
            <strong>Selected Address:</strong>{" "}
            {address && address.trim() 
              ? address 
              : "Not provided. Please add your address"}
          </p>
          <button
            className={styles.editButton}
            onClick={() => setIsAddressModalOpen(true)}
            type="button"
          >
            {address && address.trim() ? "Change Address" : "Add Address"}
          </button>
        </div>
      )}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Racket Name</th>
              <th>Item Name</th>
              <th>Service Type</th>
              <th>String Tension</th>
              <th>Item Cost</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {bookings.length > 0 ? (
              bookings.map((booking, index) => {
                const stringDetails = booking.stringId
                  ? strings.find((s) => s.string_id === booking.stringId)
                  : null;

                return (
                  <tr key={index}>
                    <td>{booking.racketName || "Not Provided"}</td>
                    <td>
                      {booking.isHybrid ? (
                        <>
                          <strong>Main:</strong> {stringDetails?.name || "BYOS"}<br/>
                          <strong>Cross:</strong> {strings.find(s => s.string_id === booking.stringIdCross)?.name || "BYOS"}
                        </>
                      ) : (
                        stringDetails?.name || "Unknown String"
                      )}
                    </td>
                    <td>{booking.stringType || "Not Selected"}</td>
                    <td>{formatTension(booking)}</td>
                    <td>
                      <span>
                        {booking.freeService ? (
                          <>
                            <span style={{ textDecoration: "line-through", color: "#888", marginRight: "8px" }}>
                              AED {getOriginalIndividualCost(booking)}
                            </span>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {getIndividualCost(booking)}
                            <div style={{ fontSize: "0.78rem", color: "#16a34a", fontWeight: "600" }}>
                              Free Service (-75 AED)
                            </div>
                          </>
                        ) : activeDiscount ? (
                          <>
                            <span style={{ textDecoration: "line-through", color: "#888", marginRight: "8px" }}>
                              AED {getOriginalIndividualCost(booking)}
                            </span>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {getIndividualCost(booking)}
                          </>
                        ) : (
                          <>
                            <span className="dirham-symbol">&#xea;</span>{" "}
                            {getIndividualCost(booking)}
                          </>
                        )}
                      </span>
                    </td>
                    <td>
                      <button
                        className={styles.editButton}
                        onClick={() => handleEdit(index)}
                      >
                        Edit
                      </button>
                      <button
                        className={styles.deleteButton}
                        onClick={() => handleDelete(index)}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="6" className={styles.emptyMessage}>
                  No bookings created yet.
                </td>
              </tr>
            )}
            {bookings.length > 0 && breakdown.pickupFee > 0 && (
              <tr className={styles.serviceFeeRow}>
                <td
                  colSpan="4"
                  style={{ textAlign: "right", fontWeight: "bold" }}
                >
                  Service Charge ({formatPickupService(pickupService)}):
                </td>
                <td style={{ fontWeight: "bold" }}>
                  <span className="dirham-symbol">&#xea;</span>{" "}
                  {breakdown.pickupFee}
                </td>
                <td></td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Coach Coupon Section */}
      {isCoach && (() => {
        const hasAnyFreeService = bookings.some(b => b.freeService === true);
        
        return (
          <div className={styles.couponSection}>
            <label htmlFor="couponCode" className={styles.couponLabel}>
              Have a coach coupon code?
            </label>
            
            {hasAnyFreeService ? (
              <div className={styles.couponDisabledNotice}>
                <span className={styles.infoIcon}>ℹ️</span>
                <span>Coach coupons cannot be used with free stringing services from racket purchases. Please use your free service first, then use coach coupons on other bookings.</span>
              </div>
            ) : (
              <div className={styles.couponInputGroup}>
                <input
                  type="text"
                  id="couponCode"
                  value={couponCode}
                  onChange={(e) => {
                    const value = e.target.value;
                    setCouponCode(value);
                    // Check if it matches the coach's actual coupon code case-insensitively
                    if (value && coachCouponCode && value.toLowerCase().trim() === coachCouponCode.toLowerCase().trim()) {
                      setCouponApplied(true);
                      setCouponDiscount(75);
                    } else {
                      setCouponApplied(false);
                      setCouponDiscount(0);
                    }
                  }}
                  placeholder="Enter code (e.g., Coach-John-1)"
                  className={styles.couponInput}
                />
                {couponApplied && (
                  <div className={styles.couponDiscountPreview}>
                    <span className={styles.discountIcon}>✓</span>
                    <span className={styles.discountText}>
                      Discount: <span className="dirham-symbol">&#xea;</span> {couponDiscount} AED
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        );
      })()}

      <div className={styles.overallCostBlock}>
        <span className={styles.overallCostText}>
          <strong>Overall Cost:</strong>{" "}
          <span className="dirham-symbol">&#xea;</span> {Math.max(0, overallTotal - couponDiscount)}
        </span>
        <button className={styles.infoButton} onClick={openBreakdown}>
          ℹ
        </button>
      </div>

      {discountDetails && discountDetails.totalSaved > 0 && (
        <div style={{
          backgroundColor: "#d4edda",
          borderColor: "#c3e6cb",
          color: "#155724",
          padding: "0.75rem 1.25rem",
          borderRadius: "8px",
          marginTop: "0.5rem",
          marginBottom: "1rem",
          fontWeight: "600",
          fontSize: "0.95rem",
          textAlign: "center",
          boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
        }}>
          🎁 Applied <strong>{discountDetails.name}</strong>: Saved <span className="dirham-symbol">&#xea;</span> {discountDetails.totalSaved}!
        </div>
      )}

      {isBreakdownPopupOpen && breakdown && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <h3>Cost Breakdown</h3>
            <p>
              <strong>Subtotal string cost:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {originalStringCost}
            </p>
            {bookings.some((b) => b.freeService) && (
              <p style={{ color: "green", fontWeight: "bold" }}>
                <strong>Free String Service:</strong>{" "}
                -<span className="dirham-symbol">&#xea;</span>{" "}
                {bookings.filter((b) => b.freeService).length * 75}
              </p>
            )}
            {discountDetails && discountDetails.totalSaved > 0 && (
              <p style={{ color: "green", fontWeight: "bold" }}>
                <strong>{discountDetails.name}:</strong>{" "}
                -<span className="dirham-symbol">&#xea;</span>{" "}
                {discountDetails.totalSaved}
              </p>
            )}
            <p>
              <strong>Total String Cost:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.stringCost}
            </p>
            {breakdown.pickupFee > 0 && (
              <p>
                <strong>
                  {(() => {
                    switch (pickupService) {
                      case "pickup":
                        return "Pickup Fee";
                      case "delivery":
                        return "Delivery Fee";
                      case "pickup/delivery":
                        return "Pickup & Delivery Fee";
                      case "express-pickup":
                        return "Express Pickup Fee";
                      case "express-delivery":
                        return "Express Delivery Fee";
                      case "express-pickup/delivery":
                        return "Express Pickup & Delivery Fee";
                      default:
                        return "Service Fee";
                    }
                  })()}
                  :
                </strong>{" "}
                <span className="dirham-symbol">&#xea;</span>{" "}
                {breakdown.pickupFee}
              </p>
            )}
            <hr />
            <p>
              <strong>Grand Total:</strong>{" "}
              <span className="dirham-symbol">&#xea;</span>{" "}
              {breakdown.grandTotal}
            </p>
            <button
              className={styles.closeModalButton}
              onClick={() => setIsBreakdownPopupOpen(false)}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {editModalOpen && selectedBookingIndex !== null && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            {(() => {
              const currentBooking = bookings[selectedBookingIndex];
              const isHybrid = !!currentBooking?.isHybrid;
              const lockType =
                currentBooking?.stringType ||
                lockedStringType ||
                "Regular";

              return (
                <>
                  <h3 style={{ marginBottom: isHybrid ? "12px" : "18px" }}>
                    {isHybrid ? "Edit Hybrid Strings" : "Select a String"} (Type Locked: {lockType})
                  </h3>

                  {isHybrid && (
                    <div style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: "10px",
                      marginBottom: "16px"
                    }}>
                      <button
                        type="button"
                        onClick={() => setHybridTab("main")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: hybridTab === "main" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "main" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                          <span style={{ fontWeight: "700", fontSize: "0.95rem", color: hybridTab === "main" ? "#0b7b43" : "#374151" }}>
                            1. Main String
                          </span>
                          {selectedStringId ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px" }}>
                              Required
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.85rem", color: selectedStringId ? "#111827" : "#9ca3af", marginTop: "4px", fontWeight: selectedStringId ? "600" : "400" }}>
                          {strings.find((s) => s.string_id === selectedStringId)?.name || "Select Main String"}
                        </span>
                      </button>

                      <button
                        type="button"
                        onClick={() => setHybridTab("cross")}
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          padding: "12px 16px",
                          borderRadius: "10px",
                          border: hybridTab === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                          background: hybridTab === "cross" ? "#f0fdf4" : "#ffffff",
                          cursor: "pointer",
                          textAlign: "left",
                          transition: "all 0.2s ease"
                        }}
                      >
                        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", alignItems: "center" }}>
                          <span style={{ fontWeight: "700", fontSize: "0.95rem", color: hybridTab === "cross" ? "#0b7b43" : "#374151" }}>
                            2. Cross String
                          </span>
                          {selectedStringIdCross ? (
                            <span style={{ background: "#0b7b43", color: "#fff", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px", fontWeight: "600" }}>
                              ✓ Selected
                            </span>
                          ) : (
                            <span style={{ background: "#e5e7eb", color: "#6b7280", fontSize: "0.7rem", padding: "1px 6px", borderRadius: "10px" }}>
                              Required
                            </span>
                          )}
                        </div>
                        <span style={{ fontSize: "0.85rem", color: selectedStringIdCross ? "#111827" : "#9ca3af", marginTop: "4px", fontWeight: selectedStringIdCross ? "600" : "400" }}>
                          {strings.find((s) => s.string_id === selectedStringIdCross)?.name || "Select Cross String"}
                        </span>
                      </button>
                    </div>
                  )}

                  {isHybrid && (
                    <div style={{ marginBottom: "10px", fontWeight: "600", color: "#0b7b43", fontSize: "0.95rem" }}>
                      Selecting: {hybridTab === "main" ? "1. Main String" : "2. Cross String"}
                    </div>
                  )}

                  <table className={styles.table}>
                    <thead>
                      <tr>
                        <th>Image</th>
                        <th>String Name</th>
                        <th>Type</th>
                        <th>Total Cost</th>
                      </tr>
                    </thead>
                    <tbody>
                      {strings.map((stringItem) => {
                        // Skip if out of stock
                        if (!stringItem.availableInAnyStore) {
                          return null;
                        }

                        const isExpress = lockType === "Express";
                        const idForRow = stringItem.string_id;
                        const costForRow = isExpress
                          ? stringItem.basePrice + 25
                          : stringItem.basePrice;
                        const isSelected = isHybrid
                          ? (hybridTab === "main" ? selectedStringId === idForRow : selectedStringIdCross === idForRow)
                          : selectedStringId === idForRow;

                        const cdnBase = process.env.REACT_APP_CDN_URL || "https://cdn11.bigcommerce.com/s-meuoh09dlj";
                        const imageUrl = stringItem.image_url
                          ? (stringItem.image_url.startsWith("http") ? stringItem.image_url : `${cdnBase}${stringItem.image_url}`)
                          : "/images/strings/default.jpg";

                        return (
                          <tr
                            key={stringItem.string_id}
                            className={isSelected ? styles.selectedRow : ""}
                            onClick={() => {
                              if (isHybrid) {
                                if (hybridTab === "main") {
                                  setSelectedStringId(idForRow);
                                  setSelectedStringType(lockType);
                                  const temp = {
                                    ...currentBooking,
                                    stringId: idForRow,
                                    stringIdCross: selectedStringIdCross,
                                    stringType: lockType,
                                  };
                                  setTotalCost(getIndividualCost(temp));
                                } else {
                                  setSelectedStringIdCross(idForRow);
                                  setSelectedStringType(lockType);
                                  const temp = {
                                    ...currentBooking,
                                    stringId: selectedStringId,
                                    stringIdCross: idForRow,
                                    stringType: lockType,
                                  };
                                  setTotalCost(getIndividualCost(temp));
                                }
                              } else {
                                setSelectedStringId(idForRow);
                                setSelectedStringType(lockType);
                                setTotalCost(costForRow);
                              }
                            }}
                            style={{ cursor: "pointer" }}
                          >
                            <td>
                              <img
                                src={imageUrl}
                                alt={stringItem.name}
                                className={styles.stringImageSmall}
                                loading="lazy"
                                onError={(e) => {
                                  e.target.onerror = null;
                                  e.target.src = "/images/strings/default.jpg";
                                }}
                              />
                            </td>
                            <td>
                              <input
                                type="radio"
                                name="selectedString"
                                value={idForRow}
                                onChange={() => {}}
                                checked={isSelected}
                              />
                              {" "}{stringItem.name} ({stringItem.brand})
                              {!isHybrid && (
                                <>
                                  {" "}(+ <span className="dirham-symbol">&#xea;</span> {costForRow})
                                </>
                              )}
                            </td>
                            <td>
                              <span className={styles.badge}>{lockType}</span>
                            </td>
                            <td>
                              {isSelected ? (
                                <span>
                                  <span className="dirham-symbol">&#xea;</span>{" "}
                                  {totalCost}
                                </span>
                              ) : (
                                ""
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>

                  {/* Tension Button */}
                  <div style={{ marginTop: "16px", marginBottom: "8px" }}>
                    <button
                      type="button"
                      onClick={() => setIsTensionModalOpen(true)}
                      className={styles.editTensionBtn}
                    >
                      <span>
                        {bookings[selectedBookingIndex]?.isHybrid ? (
                          (tensionOptionMain || tensionOptionCross) ? (
                            `Tension: Main (${tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain} ${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : tensionOptionMain === "support" ? "Support" : "Not set"}) / Cross (${tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross} ${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : tensionOptionCross === "support" ? "Support" : "Not set"})`
                          ) : "Select String Tension (Required)"
                        ) : (
                          tensionOption === "custom"
                            ? `Tension: Custom (${tensionMain || '-'} ${tensionUnit} / ${tensionCross || '-'} ${tensionUnit})`
                            : tensionOption === "standard"
                            ? "Tension: Standard"
                            : tensionOption === "support"
                            ? "Tension: Talk with Support"
                            : "Select String Tension (Required)"
                        )}
                      </span>
                      <span style={{ fontSize: "1.2rem", lineHeight: "1" }}>&#x2192;</span>
                    </button>
                  </div>
                </>
              );
            })()}

            <div className={styles.editModalActions}>
              <div className={styles.editModalButtons}>
                <button
                  type="button"
                  className={styles.cancelButton}
                  onClick={() => setEditModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="button" className={styles.saveButton} onClick={handleSaveEdit}>
                  Save Changes
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {isTensionModalOpen && (
        <div className={styles.modal} onClick={() => setIsTensionModalOpen(false)} style={{ zIndex: 10000 }}>
          <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h3 className={styles.modalTitle}>Configure String Tension</h3>
              <button 
                className={styles.modalCloseIcon} 
                onClick={() => setIsTensionModalOpen(false)}
                aria-label="Close"
              >
                &#10005;
              </button>
            </div>
            
            {bookings[selectedBookingIndex]?.isHybrid && (
              <div style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: "8px",
                marginBottom: "16px",
                background: "#f3f4f6",
                padding: "4px",
                borderRadius: "10px"
              }}>
                <button
                  type="button"
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget !== "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget !== "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget !== "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("main")}
                >
                  1. Main String {tensionOptionMain ? `(${tensionOptionMain === "custom" ? (tensionMain ? `${tensionMain}${tensionUnit}` : "Custom") : tensionOptionMain === "standard" ? "Standard" : "Support"})` : ""}
                </button>
                <button
                  type="button"
                  style={{
                    padding: "10px 12px",
                    borderRadius: "8px",
                    fontWeight: "700",
                    fontSize: "0.85rem",
                    cursor: "pointer",
                    border: tensionModalTarget === "cross" ? "2px solid #0b7b43" : "1px solid #d1d5db",
                    background: tensionModalTarget === "cross" ? "#f0fdf4" : "#ffffff",
                    color: tensionModalTarget === "cross" ? "#0b7b43" : "#4b5563",
                    transition: "all 0.2s ease"
                  }}
                  onClick={() => setTensionModalTarget("cross")}
                >
                  2. Cross String {tensionOptionCross ? `(${tensionOptionCross === "custom" ? (tensionCross ? `${tensionCross}${tensionUnit}` : "Custom") : tensionOptionCross === "standard" ? "Standard" : "Support"})` : ""}
                </button>
              </div>
            )}
            
            {(() => {
              const isHybridEdit = !!bookings[selectedBookingIndex]?.isHybrid;
              const activeTensionOption = isHybridEdit
                ? (tensionModalTarget === "cross" ? tensionOptionCross : tensionOptionMain)
                : tensionOption;

              const handleSelectTensionOption = (opt) => {
                if (isHybridEdit) {
                  if (tensionModalTarget === "cross") {
                    setTensionOptionCross(opt);
                  } else {
                    setTensionOptionMain(opt);
                  }
                } else {
                  setTensionOption(opt);
                }
              };

              return (
                <>
                  <div className={styles.formGroup}>
                    <label className={styles.label}>
                      Select Option {isHybridEdit ? `(${tensionModalTarget === "cross" ? "Cross String" : "Main String"})` : ""}
                    </label>
                    <div className={styles.tensionOptions}>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "standard" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("standard")}
                      >
                        Standard
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "custom" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("custom")}
                      >
                        Custom
                      </button>
                      <button
                        type="button"
                        className={`${styles.tensionBtn} ${
                          activeTensionOption === "support" ? styles.tensionBtnActive : ""
                        }`}
                        onClick={() => handleSelectTensionOption("support")}
                      >
                        Talk with support
                      </button>
                    </div>
                  </div>



                  {activeTensionOption === "support" && (
                    <div className={styles.tensionSupport} style={{ marginTop: "16px" }}>
                      <p>
                        Contact our support team to discuss the right tension {isHybridEdit ? `for ${tensionModalTarget === "cross" ? "Cross" : "Main"} String` : ""}.{" "}
                        <a href="tel:+971503469515" className={styles.tensionSupportLink}>
                          +971 503 469 515
                        </a>
                      </p>
                    </div>
                  )}

                  {activeTensionOption === "custom" && (
                    <div className={styles.tensionCustom}>
                      <div className={styles.unitToggle}>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "lbs" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("lbs")}
                        >
                          lbs
                        </button>
                        <button
                          type="button"
                          className={`${styles.unitBtn} ${
                            tensionUnit === "kg" ? styles.unitBtnActive : ""
                          }`}
                          onClick={() => setTensionUnit("kg")}
                        >
                          kg
                        </button>
                      </div>

                      {isHybridEdit && tensionModalTarget === "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Main String Tension: <strong>{tensionOptionMain === "custom" ? `${tensionMain || "-"} ${tensionUnit}` : tensionOptionMain === "standard" ? "Standard" : tensionOptionMain === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}
                      {isHybridEdit && tensionModalTarget !== "cross" && (
                        <div style={{
                          background: "#f8fafc",
                          border: "1px solid #e2e8f0",
                          borderRadius: "8px",
                          padding: "8px 12px",
                          fontSize: "0.85rem",
                          color: "#475569",
                          marginBottom: "12px"
                        }}>
                          Cross String Tension: <strong>{tensionOptionCross === "custom" ? `${tensionCross || "-"} ${tensionUnit}` : tensionOptionCross === "standard" ? "Standard" : tensionOptionCross === "support" ? "Support" : "Not configured yet"}</strong>
                        </div>
                      )}

                      <div className={styles.tensionFields}>
                        {(!isHybridEdit || tensionModalTarget !== "cross") ? (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              {isHybridEdit ? "Main String Tension" : "Main Tension"}
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionMain}
                                onChange={(e) => setTensionMain(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 55"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.tensionField}>
                            <label className={styles.tensionFieldLabel}>
                              Cross String Tension
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="1"
                                step="0.5"
                                value={tensionCross}
                                onChange={(e) => setTensionCross(e.target.value)}
                                className={styles.tensionInput}
                                placeholder="e.g. 53"
                              />
                              <span className={styles.tensionUnitLabel}>{tensionUnit}</span>
                            </div>
                          </div>
                        )}
                      </div>

                      {isHybridEdit ? (
                        tensionModalTarget !== "cross" ? (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabled}
                                onChange={(e) => {
                                  setPrestretchEnabled(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercent("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Main Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercent}
                                onChange={(e) => setPrestretchPercent(e.target.value)}
                                disabled={!prestretchEnabled}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabled ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabled ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        ) : (
                          <div className={styles.prestretchRow}>
                            <label
                              className={`${styles.prestretchLabel} ${
                                !prestretchEnabledCross ? styles.prestretchLabelDisabled : ""
                              }`}
                            >
                              <input
                                type="checkbox"
                                checked={prestretchEnabledCross}
                                onChange={(e) => {
                                  setPrestretchEnabledCross(e.target.checked);
                                  if (!e.target.checked) setPrestretchPercentCross("");
                                }}
                                className={styles.prestretchCheckbox}
                              />
                              Cross Prestretch
                            </label>
                            <div className={styles.tensionInputRow}>
                              <input
                                type="number"
                                min="0"
                                max="100"
                                step="0.5"
                                value={prestretchPercentCross}
                                onChange={(e) => setPrestretchPercentCross(e.target.value)}
                                disabled={!prestretchEnabledCross}
                                className={`${styles.tensionInput} ${
                                  !prestretchEnabledCross ? styles.tensionInputDisabled : ""
                                }`}
                                placeholder="e.g. 10"
                              />
                              <span
                                className={`${styles.tensionUnitLabel} ${
                                  !prestretchEnabledCross ? styles.tensionUnitDisabled : ""
                                }`}
                              >
                                %
                              </span>
                            </div>
                          </div>
                        )
                      ) : (
                        <div className={styles.prestretchRow}>
                          <label
                            className={`${styles.prestretchLabel} ${
                              !prestretchEnabled ? styles.prestretchLabelDisabled : ""
                            }`}
                          >
                            <input
                              type="checkbox"
                              checked={prestretchEnabled}
                              onChange={(e) => {
                                setPrestretchEnabled(e.target.checked);
                                if (!e.target.checked) setPrestretchPercent("");
                              }}
                              className={styles.prestretchCheckbox}
                            />
                            Prestretch
                          </label>
                          <div className={styles.tensionInputRow}>
                            <input
                              type="number"
                              min="0"
                              max="100"
                              step="0.5"
                              value={prestretchPercent}
                              onChange={(e) => setPrestretchPercent(e.target.value)}
                              disabled={!prestretchEnabled}
                              className={`${styles.tensionInput} ${
                                !prestretchEnabled ? styles.tensionInputDisabled : ""
                              }`}
                              placeholder="e.g. 10"
                            />
                            <span
                              className={`${styles.tensionUnitLabel} ${
                                !prestretchEnabled ? styles.tensionUnitDisabled : ""
                              }`}
                            >
                              %
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              );
            })()}

            <button
              type="button"
              className={styles.tensionCloseButton}
              onClick={() => setIsTensionModalOpen(false)}
            >
              Done / Close
            </button>
          </div>
        </div>
      )}

      {/* STRING: “Add another racket” button -> reveal input */}
      {bookings.length > 0 &&
        !bookings.some((b) => b.freeService === true) &&
        (!showAddStringInput ? (
          <div className={styles.formGroup}>
            <button
              className={styles.addMoreButton}
              onClick={() => setShowAddStringInput(true)}
              type="button"
            >
              + Want to add more rackets?
            </button>
          </div>
        ) : (
          <div className={styles.formGroup}>
            <input
              type="text"
              value={newRacketName}
              onChange={(e) => setNewRacketName(e.target.value)}
              className={styles.input}
              placeholder="Enter racket name"
            />
            <div className={styles.buttonGroup}>
              <button
                className={styles.addMoreButton}
                onClick={handleAddMore}
                type="button"
              >
                Add
              </button>
              <button
                className={styles.cancelButton}
                onClick={() => {
                  setShowAddStringInput(false);
                  setNewRacketName("");
                }}
                type="button"
              >
                Cancel
              </button>
            </div>
          </div>
        ))}

      <div
        id="terms-section"
        ref={termsRef}
        className={`${styles.termsContainer} ${
          termsError && !agreedToTerms ? styles.termsContainerError : ""
        }`}
      >
        <label
          className={`${styles.termsLabel} ${
            termsError && !agreedToTerms ? styles.termsLabelError : ""
          }`}
        >
          <input
            type="checkbox"
            checked={agreedToTerms}
            onChange={(e) => {
              const checked = e.target.checked;
              setAgreedToTerms(checked);
              if (checked) {
                setTermsError(false);
                setErrorMessage("");
              }
            }}
          />
          <span>
            I agree to the{" "}
            <button
              type="button"
              onClick={() => setShowTermsPopup(true)}
              className={styles.termsLink}
            >
              Terms & Conditions
            </button>
          </span>
        </label>
        {termsError && !agreedToTerms && (
          <div className={styles.termsErrorInline}>
            Please check the Terms & Conditions box before proceeding.
          </div>
        )}
      </div>

      {showTermsPopup && (
        <div className={styles.termsModal}>
          <div className={styles.termsModalContent}>
            <div dangerouslySetInnerHTML={{ __html: termsAndConditions }} />
            <div className={styles.termsModalFooter}>
              <button
                className={styles.termsCloseButton}
                onClick={() => setShowTermsPopup(false)}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className={styles.buttonContainer}>
        <button className={styles.cancelButton} onClick={() => navigate(-1)}>
          Cancel
        </button>
        <button className={styles.proceedButton} onClick={handleProceedToPay}>
          {overallTotal === 0 ? "Confirm Booking" : "Proceed to Pay"}
        </button>

        {overallTotal > 0 && (
          <PayInStoreButton
            bookings={bookings}
            customerId={customerId}
            customerName={customerName}
            pickupService={pickupService}
            address={address}
            bookingDate={bookingDate}
            instoreLocation={instoreLocation}
            agreedToTerms={agreedToTerms}
            strings={strings}
            couponCode={couponApplied ? couponCode : undefined}
            onError={showError}
          />
        )}
      </div>

      {isAddressModalOpen && (
        <div className={styles.modal}>
          <div className={styles.modalContent}>
            <button
              type="button"
              onClick={() => setIsAddressModalOpen(false)}
              className={styles.closeModalButton}
            >
              ✖
            </button>
            <h3>Select or Add Address</h3>
            <AddressForm
              onSubmit={(addr) => {
                const formattedAddress = joinAddress(
                  addr.address_line,
                  addr.area,
                  addr.pincode,
                  addr.state,
                  addr.country
                );
                setAddress(formattedAddress);
                setIsAddressModalOpen(false);
              }}
              customerId={customerId}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default CustomerBookingFinal;
